import type { DocPageContent } from '../types';

/**
 * "Chat experience" — Dashboard section (ADR-0074).
 *
 * Covers `/experience` — the controls over what a composed answer may do, the
 * library of pieces those answers are built from, and the preview that shows
 * the two together.
 *
 * It used to cover `/components` alongside it, on the reasoning that limits and
 * the library they act on are one subject read from two ends. That page is
 * retired (ADR-0539) and the reasoning survived it: the library is now listed
 * where it is ACTED on (the preference control) and the preview moved onto this
 * page, so the two ends are one surface rather than two.
 *
 * Reconciled from apps/web `features/chat-experience` and the `chatexperience.*`
 * catalog.
 */
export const DASHBOARD_EXPERIENCE_PAGE: DocPageContent = {
  slug: 'dashboard/experience',
  titleKey: 'docs.dashboard.experience.title',
  descriptionKey: 'docs.dashboard.experience.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'switch',
      textKey: 'docs.dashboard.experience.switch.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.switch.body' },

    { kind: 'heading', level: 2, id: 'opening', textKey: 'docs.dashboard.experience.open.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.open.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.dashboard.experience.open.greeting.term',
          descKey: 'docs.dashboard.experience.open.greeting.desc',
        },
        {
          termKey: 'docs.dashboard.experience.open.digest.term',
          descKey: 'docs.dashboard.experience.open.digest.desc',
        },
        {
          termKey: 'docs.dashboard.experience.open.off.term',
          descKey: 'docs.dashboard.experience.open.off.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.dashboard.experience.open.guard.title',
      textKey: 'docs.dashboard.experience.open.guard.body',
    },

    { kind: 'heading', level: 2, id: 'rules', textKey: 'docs.dashboard.experience.rules.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.rules.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.experience.rules.item.ambition',
        'docs.dashboard.experience.rules.item.kinds',
        'docs.dashboard.experience.rules.item.blocks',
      ],
    },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.dashboard.experience.rules.enforced.title',
      textKey: 'docs.dashboard.experience.rules.enforced.body',
    },

    { kind: 'heading', level: 2, id: 'hints', textKey: 'docs.dashboard.experience.hints.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.hints.body' },

    {
      kind: 'heading',
      level: 2,
      id: 'library',
      textKey: 'docs.dashboard.experience.library.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.library.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.experience.library.item.browse',
        'docs.dashboard.experience.library.item.detail',
        'docs.dashboard.experience.library.item.registered',
      ],
    },

    { kind: 'heading', level: 2, id: 'cost', textKey: 'docs.dashboard.experience.cost.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.experience.cost.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.experience.next.custom.title',
          textKey: 'docs.dashboard.experience.next.custom.text',
          to: { docSlug: 'widget/custom-ui' },
        },
        {
          titleKey: 'docs.dashboard.experience.next.design.title',
          textKey: 'docs.dashboard.experience.next.design.text',
          to: { docSlug: 'dashboard/design' },
        },
        {
          titleKey: 'docs.dashboard.experience.next.labs.title',
          textKey: 'docs.dashboard.experience.next.labs.text',
          to: { docSlug: 'dashboard/labs' },
        },
      ],
    },
  ],
};
